import { useMemo } from 'react';
import { Box, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { InputContainer } from '../../layout';
import { useTranslationContext } from '../../context/TranslationContext';
import RepeatIcon from '@mui/icons-material/Repeat';

type RecurrenceSelectProps = {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  hideLabel?: boolean; // Nueva prop para ocultar el label/container
};

// Opciones comunes de recurrencia según iCalendar RRULE
const recurrenceOptions = [
  { value: '', label: 'global.noRepeat' },
  { value: 'FREQ=DAILY', label: 'global.everyDay' },
  { value: 'FREQ=WEEKLY', label: 'global.everyWeek' },
  { value: 'FREQ=MONTHLY', label: 'global.everyMonth' },
  { value: 'FREQ=YEARLY', label: 'global.everyYear' },
  // Opciones adicionales con intervalos
  { value: 'FREQ=DAILY;INTERVAL=2', label: 'global.everyTwoDays' },
  { value: 'FREQ=WEEKLY;INTERVAL=2', label: 'global.everyTwoWeeks' },
  // Días laborables (lunes a viernes)
  { value: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR', label: 'global.everyWeekday' },
];

const RecurrenceSelect = ({
  value,
  onChange,
  disabled,
  hideLabel = false,
}: RecurrenceSelectProps) => {
  const t = useTranslationContext();

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  const menuItems = useMemo(() => {
    return recurrenceOptions.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {t(option.label, option.label)}
      </MenuItem>
    ));
  }, [t]);

  // Obtener el label correspondiente al valor actual
  const currentLabel = useMemo(() => {
    const option = recurrenceOptions.find((opt) => opt.value === value);
    return option ? t(option.label, option.label) : t('global.noRepeat');
  }, [value, t]);

  // Modo vista: mostrar como texto
  if (disabled) {
    if (hideLabel) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RepeatIcon sx={{ opacity: 0.5, color: (theme) => theme.palette.text.secondary }} fontSize="small" />
          <Typography variant="body2">{currentLabel}</Typography>
        </Box>
      );
    }

    return (
      <InputContainer title={t('global.recurrence', 'Recurrencia')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          <RepeatIcon sx={{ opacity: 0.5, color: (theme) => theme.palette.text.secondary }} fontSize="small" />
          <Typography variant="body1">{currentLabel}</Typography>
        </Box>
      </InputContainer>
    );
  }

  const selectComponent = (
    <Select
      disabled={disabled}
      size="small"
      variant="outlined"
      fullWidth
      value={value || ''}
      onChange={handleChange}
      startAdornment={<RepeatIcon sx={{ mr: 1, ml: 1, opacity: 0.5 }} fontSize="small" />}
      MenuProps={{
        PaperProps: {
          sx: {
            //@ts-ignore
            backgroundColor: (theme) => theme.palette.background.panel,
          },
        },
      }}
    >
      {menuItems}
    </Select>
  );

  // Si hideLabel es true, retornar solo el Select sin InputContainer
  if (hideLabel) {
    return selectComponent;
  }

  return (
    <InputContainer title={t('global.recurrence', 'Recurrencia')}>{selectComponent}</InputContainer>
  );
};

export default RecurrenceSelect;
